import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';

import { IconLoader2, IconPencil } from '@tabler/icons-react';

import {
  ButtonField,
  ButtonGroup,
  ButtonGroupItem,
  InputField,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectField,
  Tag,
} from '@wk/components-react16';

import useToast from '@/shared/stores/toastStore';
import { getAssignableRoles } from '@/domains/access/userAccessApi';
import { getViews, upsertView } from '@/domains/access/systemConfigApi';
import { getSystemCheckpointConfigurations } from '@/domains/checkpoint/checkpointApi';

const DEFAULT_VIEW = 'default';
const PUBLIC_OPTION = ''; // requiredAccessRole = null
const APP_ONLY_OPTION = '*'; // any app-only caller

/**
 * A view row for the access-mapping grid. Views are NOT authored here — they are
 * derived from the Conformance configuration:
 *   - `inRegistry`: present in SystemConfiguration.views (carries the role mapping)
 *   - `checkpointCount`: number of distinct checkpoints assigned to the view
 *
 * The list is the union of both sources, so the admin can map a view that is in use
 * but not yet in the registry, and can see a registered view that has no checkpoints.
 * The only mutable field is `requiredAccessRole` (the view ↔ Entra App Role mapping).
 */
interface ViewRow {
  name: string;
  requiredAccessRole: string | null;
  inRegistry: boolean;
  checkpointCount: number;
}

const apiErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as AxiosError | undefined)?.response?.data as
    | { title?: string; message?: string }
    | undefined;
  return data?.title || data?.message || fallback;
};

const accessLabel = (
  requiredAccessRole: string | null,
  roleLabel: (v: string) => string,
) => {
  if (!requiredAccessRole) return 'Everyone';
  if (requiredAccessRole === APP_ONLY_OPTION) return 'Any app-only caller';
  return roleLabel(requiredAccessRole);
};

const ViewsTab = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [filterTerm, setFilterTerm] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [name, setName] = useState('');
  const [requiredRole, setRequiredRole] = useState<string>(PUBLIC_OPTION);

  // Below the `sm` breakpoint AG Grid has no height to fill, so render a card list instead.
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window === 'undefined'
      ? false
      : window.matchMedia('(max-width: 640px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
    setIsMobileViewport(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Registry views (carry the role mapping) — SystemConfiguration.views.
  const viewsQ = useQuery({
    queryKey: ['system', 'views'],
    queryFn: async () => (await getViews()).data,
  });
  // Data-driven views — derived from the views assigned to checkpoint configs.
  const checkpointsQ = useQuery({
    queryKey: ['system', 'checkpoint-configs'],
    queryFn: async () => (await getSystemCheckpointConfigurations()).data?.data ?? [],
    staleTime: 5 * 60 * 1000,
  });
  const rolesQ = useQuery({
    queryKey: ['user-access', 'roles'],
    queryFn: async () => (await getAssignableRoles()).data,
    staleTime: 60 * 60 * 1000,
  });

  const roleLabel = (v: string) =>
    rolesQ.data?.find((r) => r.value === v)?.displayName ?? v;
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['system', 'views'] });

  // Distinct-checkpoint count per view name, derived from the checkpoint configs.
  const viewUsage = useMemo(() => {
    const sets = new Map<string, Set<string>>();
    (checkpointsQ.data ?? []).forEach((cfg, index) => {
      const id =
        String((cfg.checkpointId ?? cfg.id ?? '') as string).trim() ||
        `__cfg-${index}`;
      const views = Array.isArray(cfg.views) ? cfg.views : [];
      for (const view of views) {
        if (!view) continue;
        if (!sets.has(view)) sets.set(view, new Set());
        sets.get(view)!.add(id);
      }
    });
    const counts = new Map<string, number>();
    for (const [view, ids] of sets) counts.set(view, ids.size);
    return counts;
  }, [checkpointsQ.data]);

  // Union of registry views and data-driven views, keyed by name.
  const rows = useMemo<ViewRow[]>(() => {
    const byName = new Map<string, ViewRow>();
    for (const v of viewsQ.data ?? []) {
      byName.set(v.name, {
        name: v.name,
        requiredAccessRole: v.requiredAccessRole ?? null,
        inRegistry: true,
        checkpointCount: 0,
      });
    }
    for (const [view, count] of viewUsage) {
      const existing = byName.get(view);
      if (existing) existing.checkpointCount = count;
      else
        byName.set(view, {
          name: view,
          requiredAccessRole: null,
          inRegistry: false,
          checkpointCount: count,
        });
    }
    return [...byName.values()];
  }, [viewsQ.data, viewUsage]);

  const filteredRows = useMemo(() => {
    const needle = filterTerm.trim().toLowerCase();
    return [...rows]
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter((v) => !needle || v.name.toLowerCase().includes(needle));
  }, [rows, filterTerm]);

  const sortedRoles = useMemo(
    () =>
      [...(rolesQ.data ?? [])].sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      ),
    [rolesQ.data],
  );

  const isLoading = viewsQ.isLoading || checkpointsQ.isLoading;

  const upsertMutation = useMutation({
    mutationFn: async ({
      viewName,
      requiredAccessRole,
    }: {
      viewName: string;
      requiredAccessRole: string | null;
    }) => (await upsertView(viewName, { requiredAccessRole })).data,
    onSuccess: (saved) => {
      invalidate();
      showToast('success', {
        title: 'Mapping saved',
        message: `Access mapping for "${saved.name}" updated.`,
      });
      setEditOpen(false);
    },
    onError: (error) =>
      showToast('error', {
        title: 'Could not save mapping',
        message: apiErrorMessage(
          error,
          'An error occurred while saving the access mapping.',
        ),
      }),
  });

  const openEdit = (view: ViewRow) => {
    setName(view.name);
    setRequiredRole(view.requiredAccessRole ?? PUBLIC_OPTION);
    setEditOpen(true);
  };

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    upsertMutation.mutate({
      viewName: trimmed,
      requiredAccessRole: requiredRole === PUBLIC_OPTION ? null : requiredRole,
    });
  };

  const renderAccess = (v: ViewRow) => {
    if (!v.inRegistry) {
      return (
        <span
          className="inline-flex items-center rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700"
          title="No role mapping yet — the view is open to everyone. Edit to map an Entra App Role."
        >
          Unmapped
        </span>
      );
    }
    const isPublic = !v.requiredAccessRole;
    return (
      <span
        className={`inline-flex items-center rounded px-2 py-0.5 text-xs ${
          isPublic ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
        }`}
      >
        {accessLabel(v.requiredAccessRole, roleLabel)}
      </span>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        Views come from the Conformance checkpoint configurations and cannot be created
        or deleted here. For each view you can map the Entra App Role required to access
        it.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="cg-field flex min-w-0 flex-1 flex-col sm:max-w-xs">
          <label className="cg-field-label" htmlFor="view-filter">
            Filter
          </label>
          <div className="group border border-input text-sm hover:border-[#005b92] focus-within:border-[#000]">
            <div className="flex min-h-8 items-center gap-1 px-[15px] py-1">
              <input
                id="view-filter"
                type="text"
                value={filterTerm}
                onChange={(e) => setFilterTerm(e.target.value)}
                placeholder="Filter by name"
                autoComplete="off"
                className="ml-2 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>
        </div>
      </div>

      {isMobileViewport ? (
        <div className="flex flex-col gap-3">
          {isLoading ? (
            <div className="flex min-h-[120px] items-center justify-center text-gray-500">
              <IconLoader2 size={20} className="animate-spin" />
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="rounded-md border bg-white p-4 text-sm text-gray-600">
              No views found.
            </div>
          ) : (
            filteredRows.map((v) => {
              const isDefault = v.name === DEFAULT_VIEW;
              return (
                <article key={v.name} className="rounded-lg border bg-white p-3 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 font-semibold leading-5">
                        {v.name}
                        {isDefault ? <Tag color="gray">default</Tag> : null}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {v.checkpointCount > 0
                          ? `${v.checkpointCount} checkpoint(s)`
                          : 'No checkpoints'}
                      </p>
                      <span className="mt-1 inline-flex">{renderAccess(v)}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        aria-label={`Edit mapping for ${v.name}`}
                        title={
                          isDefault
                            ? "The 'default' view is always public"
                            : 'Edit access mapping'
                        }
                        disabled={isDefault}
                        onClick={() => !isDefault && openEdit(v)}
                        className={`flex h-7 w-7 items-center justify-center ${
                          isDefault
                            ? 'cursor-not-allowed text-gray-300'
                            : 'text-gray-500 hover:text-gray-800'
                        }`}
                      >
                        <IconPencil size={16} />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </div>
      ) : (
      <div className="cg-ag-grid-field ag-theme-balham min-h-0 flex-1">
        <AgGridReact
          animateRows={false}
          loading={isLoading}
          rowData={filteredRows}
          columnDefs={[
            {
              headerName: 'View',
              field: 'name',
              flex: 2,
              minWidth: 200,
              autoHeight: true,
              cellRenderer: (p: CustomCellRendererProps<ViewRow>) => {
                const v = p.data;
                if (!v) return null;
                return (
                  <div className="flex items-center gap-2 py-1">
                    <span className="font-medium">{v.name}</span>
                    {v.name === DEFAULT_VIEW ? <Tag color="gray">default</Tag> : null}
                  </div>
                );
              },
            },
            {
              headerName: 'Checkpoints',
              colId: 'checkpoints',
              width: 130,
              cellRenderer: (p: CustomCellRendererProps<ViewRow>) => {
                const v = p.data;
                if (!v) return null;
                return v.checkpointCount > 0 ? (
                  <span className="text-sm text-gray-700">{v.checkpointCount}</span>
                ) : (
                  <span
                    className="text-sm text-gray-400"
                    title="No checkpoints reference this view"
                  >
                    —
                  </span>
                );
              },
            },
            {
              headerName: 'Access',
              colId: 'access',
              flex: 2,
              minWidth: 200,
              sortable: false,
              cellRenderer: (p: CustomCellRendererProps<ViewRow>) => {
                const v = p.data;
                if (!v) return null;
                return renderAccess(v);
              },
            },
            {
              headerName: 'Actions',
              colId: 'actions',
              width: 110,
              sortable: false,
              cellRenderer: (p: CustomCellRendererProps<ViewRow>) => {
                const v = p.data;
                if (!v) return null;
                const isDefault = v.name === DEFAULT_VIEW;
                return (
                  <div className="flex h-full items-center gap-1">
                    <button
                      type="button"
                      aria-label={`Edit mapping for ${v.name}`}
                      title={
                        isDefault
                          ? "The 'default' view is always public"
                          : 'Edit access mapping'
                      }
                      disabled={isDefault}
                      onClick={() => !isDefault && openEdit(v)}
                      className={`flex h-7 w-7 items-center justify-center ${
                        isDefault
                          ? 'cursor-not-allowed text-gray-300'
                          : 'cursor-pointer text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      <IconPencil size={16} />
                    </button>
                  </div>
                );
              },
            },
          ]}
        />
      </div>
      )}
      <p className="text-sm text-muted-foreground">{filteredRows.length} view(s)</p>

      {/* Edit the view ↔ Entra App Role mapping (the view itself is read-only). */}
      <Modal
        isOpen={editOpen}
        mobileMode="fullscreen"
        srLabelledBy="view-title"
        srDescribedBy="view-desc"
        onUserRequest={() => setEditOpen(false)}
      >
        <ModalHeader titleId="view-title" onUserRequest={() => setEditOpen(false)}>
          Edit access mapping
        </ModalHeader>
        <ModalBody descriptionId="view-desc">
          <div className="flex flex-col gap-3">
            <InputField label="View">
              <input
                type="text"
                name="view-name"
                value={name}
                disabled
                autoComplete="off"
              />
            </InputField>
            <SelectField label="Required access role">
              <select
                value={requiredRole}
                onChange={(e) => setRequiredRole(e.target.value)}
              >
                <option value={PUBLIC_OPTION}>Everyone (public)</option>
                <option value={APP_ONLY_OPTION}>Any app-only caller (*)</option>
                {sortedRoles.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.displayName}
                  </option>
                ))}
              </select>
            </SelectField>
            <p className="text-xs text-gray-500">
              Select an Entra App Role to gate the view, or "Everyone (public)" to remove
              the mapping. Checkpoints are assigned to a view from the Checkpoint Settings
              screen.
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup mode="static">
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button
                  type="button"
                  onClick={() => setEditOpen(false)}
                  disabled={upsertMutation.isPending}
                >
                  Cancel
                </button>
              </ButtonField>
            </ButtonGroupItem>
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField>
                <button
                  type="button"
                  disabled={upsertMutation.isPending || !name.trim()}
                  onClick={submit}
                >
                  Save
                  {upsertMutation.isPending ? (
                    <IconLoader2 size={16} className="animate-spin" />
                  ) : null}
                </button>
              </ButtonField>
            </ButtonGroupItem>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

    </div>
  );
};

export default ViewsTab;
